// Un Admin règle les préférences personnelles d'un autre utilisateur (menu Affichage, couleurs, colonnes…).
// Gardé pour cet onglet seulement, jusqu'à « Terminer » ou la déconnexion. Les données et les droits restent ceux de l'Admin.
const STORAGE_KEY = "grain.preferences-for";
const RETURN_URL_KEY = "grain.preferences-return-url";
export const PREFERENCES_FOR_HEADER = "X-Grain-Preferences-For";
const DELEGATED_URL = /^\/api\/(?:me\/ui-preferences(?:[/?]|$)|data-manager\/(?:column-preferences\/|state(?:[?]|$))|billing\/consolidation\/geotab\/preferences(?:[?]|$)|product-updates\/codex\/models?(?:[?]|$))/;
const flushHandlers = new Set();

export function onPreferencesLeave(handler) {
  flushHandlers.add(handler);
  return () => flushHandlers.delete(handler);
}

function afterPreferencesSaved(action) {
  const pending = [...flushHandlers].map((handler) => handler()).filter((result) => result?.then);
  if (!pending.length) return action();
  // En cas d'échec, rester dans le compte courant ; le lecteur concerné affiche son erreur.
  return Promise.all(pending).then(action).catch(() => {});
}

export function readPreferencesTarget() {
  try {
    const target = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null");
    return target?.userId ? target : null;
  } catch { return null; }
}

export function preferencesTargetHeaderFor(url) {
  const target = readPreferencesTarget();
  return target && isUserUiPreferencesUrl(url) ? target.userId : "";
}

export function isUserUiPreferencesUrl(url) {
  return DELEGATED_URL.test(String(url || "").replace(/^https?:\/\/[^/]+/, ""));
}

// Rechargement complet : chaque réglage est relu pour l'utilisateur choisi.
export function startPreferencesTarget({ userId, email = "", roleLabel = "" }, { assign = (url) => window.location.assign(url) } = {}) {
  return afterPreferencesSaved(() => {
    window.sessionStorage.setItem(RETURN_URL_KEY, `${window.location?.pathname || "/admin"}${window.location?.search || ""}${window.location?.hash || ""}`);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, email, roleLabel }));
    assign("/results/latest");
  });
}

export function endPreferencesTarget({ reload = (url) => window.location.assign(url) } = {}) {
  return afterPreferencesSaved(() => {
    const storedUrl = window.sessionStorage.getItem(RETURN_URL_KEY) || "/admin";
    const returnUrl = storedUrl.startsWith("/") && !storedUrl.startsWith("//") ? storedUrl : "/admin";
    clearPreferencesTarget();
    // Revenir à la page de l'Admin évite d'enregistrer chez lui les dates ouvertes pour l'autre compte.
    reload(returnUrl);
  });
}

// Pages dont l'Admin peut remettre les réglages comme à la première visite ; dans le Data Manager, la table ouverte.
const RESET_PAGES = { "/results": ["dispatch", "Dispatch"], "/consolidation": ["consolidation", "Consolidation"], "/billing": ["billing", "Facturation"],
  "/geotab": ["geotab", "Geotab"], "/reports": ["reports", "Rapports"], "/updates": ["journal", "Journal"] };
const RESET_TABLES = { orders: "Commandes", vehicles: "Camions", compartments: "Remorques", drivers: "Chauffeurs", employees: "Chauffeurs",
  correspondences: "Correspondances", products: "Produits" };

export function preferencesResetPage(pathname = "", search = "") {
  const root = `/${String(pathname).split("/")[1] || ""}`;
  if (RESET_PAGES[root]) return { page: RESET_PAGES[root][0], label: RESET_PAGES[root][1] };
  if (root !== "/data-manager") return null;
  const params = new URLSearchParams(search);
  const tab = String(params.get("tab") || "orders").trim().toLowerCase();
  return RESET_TABLES[tab] ? { page: "data_manager", table: tab === "employees" ? "drivers" : tab, label: `Data Manager · ${RESET_TABLES[tab]}` } : null;
}

// Les modifications en attente partent d'abord : elles ne doivent pas réécrire les réglages remis par défaut.
export async function resetPreferencesPage({ page, table }, { fetchImpl, reload = () => window.location.reload() }) {
  await Promise.all([...flushHandlers].map((handler) => handler()));
  const response = await fetchImpl("/api/me/ui-preferences/reset", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(table ? { page, table } : { page }),
  });
  if (!response.ok) throw new Error("La réinitialisation n'a pas été enregistrée. Réessayez.");
  reload();
}

export function clearPreferencesTarget() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_URL_KEY);
  } catch { /* Rien à retirer si le stockage est indisponible. */ }
}

// Les réglages peuvent être changés par un Admin : relus quand l'utilisateur revient sur l'onglet du SaaS.
const tabReturnHandlers = new Set();
let listening = false;
let lastTabReturnAt = 0;
function handleTabReturn() {
  if (document.visibilityState !== "visible" || Date.now() - lastTabReturnAt < 5000) return;
  lastTabReturnAt = Date.now();
  tabReturnHandlers.forEach((handler) => { try { handler(); } catch { /* Un lecteur en échec n'arrête pas les autres. */ } });
}

export function onPreferencesTabReturn(handler) {
  if (typeof document === "undefined") return () => {};
  if (!listening) {
    listening = true;
    document.addEventListener("visibilitychange", handleTabReturn);
    window.addEventListener("focus", handleTabReturn);
  }
  tabReturnHandlers.add(handler);
  return () => tabReturnHandlers.delete(handler);
}
